import { authUrl } from '@/config/api';
import { apiFetch } from '@/lib/apiFetch';

export type SystemApiKeyView = {
  id: string;
  user_id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  status: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
};

export type CreateSystemApiKeyBody = {
  name: string;
  scopes?: string[];
  expires_days?: number | null;
};

export type CreateSystemApiKeyResponse = {
  api_key: string;
  key: SystemApiKeyView;
};

export type ListSystemApiKeysResponse = {
  keys: SystemApiKeyView[];
};

async function parseApiError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const payload = JSON.parse(text) as { message?: string; detail?: unknown };
    if (payload.message) return payload.message;
    if (typeof payload.detail === 'string') return payload.detail;
    if (Array.isArray(payload.detail)) {
      return payload.detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: string }).msg ?? '');
          }
          return '';
        })
        .filter(Boolean)
        .join('; ');
    }
  } catch {
    /* raw text */
  }
  return text || res.statusText;
}

export async function listSystemApiKeys(): Promise<SystemApiKeyView[]> {
  const res = await apiFetch(authUrl('api-keys'));
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  const payload = (await res.json()) as ListSystemApiKeysResponse;
  return payload.keys ?? [];
}

export async function createSystemApiKey(body: CreateSystemApiKeyBody): Promise<CreateSystemApiKeyResponse> {
  const res = await apiFetch(authUrl('api-keys'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  return (await res.json()) as CreateSystemApiKeyResponse;
}

export async function revokeSystemApiKey(keyId: string): Promise<void> {
  const res = await apiFetch(authUrl(`api-keys/${encodeURIComponent(keyId)}`), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
}
